import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    FiArrowLeft,
    FiSave,
    FiAlertCircle,
    FiCheckCircle,
    FiRefreshCw,
    FiMail,
    FiUsers
} from 'react-icons/fi';
import { getCoordinatorById, updateCoordinator, getAllCoordinators } from './coordinatorService';
import { getAllGuilds } from '../guilds/guildService';
import { sendPasswordResetEmail } from '../firebase';
import './CreateCoordinator.css'; // Reusing CreateCoordinator CSS for form layout

export default function EditCoordinator() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        assignedGuilds: [],
    });

    const [guilds, setGuilds] = useState([]);
    // guildIds already assigned to OTHER coordinators
    const [takenGuildIds, setTakenGuildIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadData();
    }, [id]);

    async function loadData() {
        if (!id) return;
        try {
            setError('');
            setLoading(true);
            const [coordinator, guildsData, allCoordinators] = await Promise.all([
                getCoordinatorById(id),
                getAllGuilds(),
                getAllCoordinators(),
            ]);

            if (coordinator) {
                setFormData({
                    fullName: coordinator.fullName || '',
                    email: coordinator.email || '',
                    phone: coordinator.phone || '',
                    assignedGuilds: coordinator.assignedGuilds || [],
                });
            } else {
                setError('Coordinator not found');
            }
            setGuilds(guildsData);

            // Build set of guild IDs already assigned to OTHER coordinators
            const taken = new Set();
            allCoordinators.forEach(c => {
                if (c.id !== id && c.uid !== id) {
                    (c.assignedGuilds || []).forEach(gid => taken.add(gid));
                }
            });
            setTakenGuildIds(taken);
        } catch (err) {
            console.error('Error loading coordinator data:', err);
            setError('Failed to load coordinator data');
        } finally {
            setLoading(false);
        }
    }

    function handleChange(e) {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setError('');
        setSuccess('');
    }

    function handleGuildToggle(guildId) {
        const currentGuilds = [...formData.assignedGuilds];
        const index = currentGuilds.indexOf(guildId);

        if (index > -1) {
            currentGuilds.splice(index, 1);
        } else {
            currentGuilds.push(guildId);
        }

        setFormData({
            ...formData,
            assignedGuilds: currentGuilds,
        });
        setError('');
        setSuccess('');
    }

    async function handleSubmit(e) {
        e.preventDefault();

        if (!formData.fullName || formData.assignedGuilds.length === 0) {
            setError('Please fill in all required fields and select at least one guild');
            return;
        }

        try {
            setSaving(true);
            setError('');
            setSuccess('');

            const { email, ...updates } = formData;
            await updateCoordinator(id, updates);
            setSuccess('Coordinator profile updated successfully!');

            setTimeout(() => {
                navigate('/dashboard/coordinators');
            }, 1500);
        } catch (err) {
            console.error('Error updating coordinator:', err);
            setError(err.message || 'Failed to update coordinator profile');
        } finally {
            setSaving(false);
        }
    }

    async function handleSendInvite() {
        try {
            setInviting(true);
            setError('');
            setSuccess('');

            const { auth } = await import('../firebase');
            await sendPasswordResetEmail(auth, formData.email);

            setSuccess('An invitation (password reset) email has been sent successfully.');
        } catch (err) {
            console.error('Error sending invite:', err);
            setError('Failed to send invitation email. Please try again.');
        } finally {
            setInviting(false);
        }
    }

    if (loading) {
        return (
            <div className="page-container">
                <div className="loading-spinner">
                    <FiRefreshCw className="spin-icon" />
                    <p>Loading coordinator profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left">
                    <button className="btn-icon" onClick={() => navigate('/dashboard/coordinators')}>
                        <FiArrowLeft />
                    </button>
                    <h1>Edit Coordinator</h1>
                </div>
                <div className="header-actions">
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleSendInvite}
                        disabled={inviting || saving}
                    >
                        {inviting ? <FiRefreshCw className="spin-icon" /> : <FiMail />}
                        <span>Send Invitation</span>
                    </button>
                </div>
            </div>

            <div className="form-container">
                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="alert alert-error">
                            <FiAlertCircle />
                            <span>{error}</span>
                        </div>
                    )}

                    {success && (
                        <div className="alert alert-success">
                            <FiCheckCircle />
                            <span>{success}</span>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="fullName">
                            <FiUsers />
                            <span>Full Name *</span>
                        </label>
                        <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            required
                            disabled={saving}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">
                            <FiUsers />
                            <span>Phone Number *</span>
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            disabled={saving}
                        />
                    </div>

                    <div className="form-group">
                        <label>Email (Read-only)</label>
                        <input
                            type="email"
                            value={formData.email}
                            disabled
                            className="input-disabled"
                        />
                    </div>

                    <div className="form-group">
                        <label>
                            <FiUsers />
                            <span>Assigned Guilds *</span>
                        </label>
                        <div className="guilds-selection-grid">
                            {guilds.map((guild) => {
                                const isTaken = takenGuildIds.has(guild.id);
                                const isSelected = formData.assignedGuilds.includes(guild.id);
                                return (
                                    <div
                                        key={guild.id}
                                        className={`guild-checkbox-item ${isSelected ? 'selected' : ''} ${isTaken ? 'disabled' : ''}`}
                                        onClick={() => !saving && !isTaken && handleGuildToggle(guild.id)}
                                        title={isTaken ? 'Already assigned to another coordinator' : ''}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => { }}
                                            disabled={saving || isTaken}
                                        />
                                        <span>{guild.name}</span>
                                        {isTaken && <span className="taken-badge">Assigned</span>}
                                    </div>
                                );
                            })}
                        </div>
                        {guilds.length === 0 && <p className="empty-guilds-msg">No guilds found.</p>}
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={() => navigate('/dashboard/coordinators')}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <FiRefreshCw className="spin-icon" />
                                    <span>Saving Changes...</span>
                                </>
                            ) : (
                                <>
                                    <FiSave />
                                    <span>Save Profile</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
